import { Dialog as DialogPrimitive } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { DialogCloseButton } from './dialog';
import { useReturnFocus } from './focus';

type DrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Names the drawer (rendered as its h2). Asset names: pass `titleClassName="ident"`. */
  title: ReactNode;
  titleClassName?: string;
  /** Header content before the title (e.g. a "← Back" button inside a drawer). */
  headerStart?: ReactNode;
  /** Extra header content next to the title (e.g. a status pill). */
  headerExtra?: ReactNode;
  footer?: ReactNode;
  side?: 'right' | 'left';
  /** CSS width; defaults to the --drawer token, capped at the viewport. */
  width?: string;
  /** Hide the visible header (mobile navigation drawer). The title stays for screen readers. */
  bare?: boolean;
  /** Where focus goes on open; defaults to the first focusable element. */
  onOpenAutoFocus?: (event: Event) => void;
  /**
   * Where focus goes on close. Defaults to the element focused when the drawer
   * opened; pass an explicit target when the opener is made inert while open.
   */
  returnFocusTo?: { current: HTMLElement | null };
  className?: string;
  children: ReactNode;
};

/**
 * An overlay panel that slides in from the edge. It never reflows the page
 * (fixed position over a scrim), traps focus while open, closes on Esc or
 * scrim click, and returns focus to whatever opened it. Controlled only: the
 * owner decides when it opens (e.g. after a row click), so it is never open
 * by default.
 */
export function Drawer({ open, onOpenChange, title, titleClassName, headerStart, headerExtra, footer, side = 'right', width, bare = false, onOpenAutoFocus, returnFocusTo, className, children }: DrawerProps) {
  const returnFocus = useReturnFocus(returnFocusTo);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-scrim data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onOpenAutoFocus={(event) => {
            returnFocus.onOpenAutoFocus();
            onOpenAutoFocus?.(event);
          }}
          onCloseAutoFocus={returnFocus.onCloseAutoFocus}
          style={{ width: `min(${width ?? 'var(--drawer)'}, 100vw)` }}
          className={cn(
            'fixed top-0 bottom-0 z-41 flex flex-col bg-surface text-fg shadow-pop outline-none',
            side === 'right'
              ? 'right-0 border-l border-line data-[state=closed]:animate-drawer-out-right data-[state=open]:animate-drawer-in-right'
              : 'left-0 border-r border-line data-[state=closed]:animate-drawer-out-left data-[state=open]:animate-drawer-in-left',
            className,
          )}
        >
          {bare ? (
            <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
          ) : (
            <div className="flex items-center gap-2 border-b border-line px-6 py-[18px]">
              {headerStart}
              <DialogPrimitive.Title className={cn('m-0 min-w-0 flex-1 text-base font-semibold break-all', titleClassName)}>
                {title}
              </DialogPrimitive.Title>
              {headerExtra}
              <DialogCloseButton />
            </div>
          )}
          <div className={cn('min-h-0 flex-1 overflow-y-auto', !bare && 'flex flex-col gap-5 p-6')}>{children}</div>
          {footer ? <div className="flex flex-wrap gap-2 border-t border-line px-6 py-4">{footer}</div> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
